import { cva } from "class-variance-authority";

import { BASE_ROLES, FILL_ROLES } from "../../lib/colors";

/**
 * The roles Button offers, and the source of both its `cva` keys and `ButtonProps["color"]` — so
 * the two cannot drift apart. `muted` is handled outside the registry-driven loops below (Button
 * tones its fill differently from Badge), but it is still one of Button's roles.
 */
export const BUTTON_ROLES = [...BASE_ROLES, "success", "warning", "info", "ai"] as const;

export type ButtonColor = (typeof BUTTON_ROLES)[number];

/**
 * The roles whose classes come straight from the registry. `muted` is excluded because Button tones
 * its fill differently from the shared definition (a `--foreground` ink on a lighter `/80` hover),
 * so it is spelled out per form factor below. Excluded rather than overridden: cva *concatenates*
 * every matching compound variant, so emitting both would leave two conflicting inks on the element
 * for tailwind-merge to arbitrate.
 */
const REGISTRY_DRIVEN_ROLES = BUTTON_ROLES.filter((role) => role !== "muted");

export const buttonVariants = cva(
  // structural
  "inline-flex items-center justify-center gap-2 shrink-0 whitespace-nowrap rounded-[calc(var(--radius)-2px)] " +
    // every variant reserves a 1px border box (transparent by default) so switching to `outline`
    // — which only sets the border color — never shifts size or layout.
    "border border-transparent " +
    "text-sm font-medium transition-all outline-none " +
    // stateful
    "disabled:pointer-events-none disabled:opacity-50 " +
    "focus-visible:ring-ring/50 focus-visible:ring-[3px] " +
    "aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 " +
    // svg children — structural plain, size guarded so a consumer's own size-* on the icon wins
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 " +
    // Icons take the button's ink. `fill-current` covers fill-based/brand SVGs, which would
    // otherwise paint their own hardcoded color. Stroke icons (lucide) declare `fill="none"` and
    // follow `currentColor` through their stroke; they are excluded because a CSS `fill` beats
    // that presentation attribute and would paint the whole glyph solid.
    // Guarded the same way as the size above, so a consumer recolors by putting a text-*/fill-*
    // class on the icon itself rather than out-specifying this selector.
    "[&_svg:not([class*='text-'])]:text-current [&_svg:not([class*='fill-']):not([fill=none])]:fill-current " +
    // while loading, hide every child svg so only the leading Spinner (a div, not an svg) shows.
    // Single arbitrary selector (not stacked data-*:[&_svg]:) so Tailwind v3 reliably emits it.
    "[&[data-loading=true]_svg]:hidden",
  {
    variants: {
      variant: {
        solid: "shadow-xs",
        // Flat, unlike solid/outline: a soft fill is a tinted surface the control sits *in* rather
        // than a raised element, and a shadow under a pale tint reads as a rendering artifact.
        soft: "",
        outline: "border-border bg-background shadow-xs",
        ghost: "",
        link: "underline-offset-4 hover:underline",
      },
      // Keys only — every color pixel lives in the compoundVariants below, composed from the shared
      // registry. `satisfies` ties these keys to ButtonColor, so adding a role to BUTTON_ROLES
      // without adding it here (or vice versa) is a type error rather than a silent gap.
      color: {
        primary: "",
        secondary: "",
        destructive: "",
        muted: "",
        white: "",
        success: "",
        warning: "",
        info: "",
        ai: "",
      } satisfies Record<ButtonColor, "">,
      size: {
        xs: "h-7 gap-1 px-2 text-xs [&_svg:not([class*='size-'])]:size-3.5",
        sm: "h-8 gap-1.5 px-2",
        md: "h-9 px-3",
        lg: "h-10 px-4",
      },
      iconOnly: {
        true: "p-0",
        false: "",
      },
    },
    compoundVariants: [
      // solid — filled background, on-color foreground. Surface + ink come from the shared registry;
      // `muted` is the one role whose fill Button tones differently from Badge (a --foreground ink
      // on a lighter /80 hover, rather than the muted ink), so it stays spelled out here.
      ...REGISTRY_DRIVEN_ROLES.map((color) => ({
        variant: "solid" as const,
        color,
        class: `${FILL_ROLES[color].solidBase} ${FILL_ROLES[color].solidHover}`,
      })),
      { variant: "solid", color: "muted", class: "bg-muted text-foreground hover:bg-muted/80" },
      // outline — colored border + text, tinted hover
      ...REGISTRY_DRIVEN_ROLES.map((color) => ({
        variant: "outline" as const,
        color,
        class: `${FILL_ROLES[color].outlineBase} ${FILL_ROLES[color].outlineHover}`,
      })),
      { variant: "outline", color: "muted", class: "text-muted-foreground hover:bg-muted" },
      // soft — a quiet tinted fill, always visible, written in the role's own saturated ink
      ...REGISTRY_DRIVEN_ROLES.map((color) => ({
        variant: "soft" as const,
        color,
        class: `${FILL_ROLES[color].softBase} ${FILL_ROLES[color].softHover}`,
      })),
      // `muted` keeps Button's own tone here for the same reason it does under `solid`: the shared
      // definition writes in the muted ink, where Button wants the stronger `--foreground`.
      { variant: "soft", color: "muted", class: "bg-muted text-foreground hover:bg-muted/80" },
      // ghost — no background until hover. Shares the outline ink/hover for most roles; `secondary`
      // holds its own ink through the hover rather than swapping to accent-foreground.
      // `primary` keeps its ink through the hover too: swapping to accent-foreground on hover would
      // drop a branded primary back to neutral grey exactly when the button is being interacted with.
      // Ghost has no border box to color, so it takes the primary ink without the
      // `border-primary/40` that `outlineBase` carries.
      //
      // The neutral hovers below are an alpha of `--foreground` rather than the opaque `--accent`
      // they used to be: a ghost button is transparent at rest, so it routinely sits on a tinted
      // surface (a card, a colored callout, the ai confirmation bubble), and an opaque grey hover
      // *replaces* that surface instead of shading it -- reading as a hole punched in the tint. The
      // role-tinted hovers (destructive, white, ai, status) were already translucent for the same
      // reason. Dark mode needs a heavier alpha: a 5% white veil on a near-black surface is barely
      // perceptible, where 5% black on white is plenty.
      { variant: "ghost", color: "primary", class: "text-primary hover:bg-foreground/5 dark:hover:bg-foreground/10" },
      {
        variant: "ghost",
        color: "secondary",
        class:
          "text-secondary-foreground hover:bg-foreground/5 hover:text-secondary-foreground dark:hover:bg-foreground/10",
      },
      // Ghost has no border box to color, so it takes the destructive ink and hover without the
      // `border-destructive/40` that `outlineBase` carries.
      { variant: "ghost", color: "destructive", class: `text-destructive ${FILL_ROLES.destructive.outlineHover}` },
      {
        variant: "ghost",
        color: "muted",
        class: "text-muted-foreground hover:bg-foreground/5 dark:hover:bg-foreground/10",
      },
      { variant: "ghost", color: "white", class: "text-white hover:bg-white/10" },
      // Ghost has no border box to color, so it takes the ai ink without the `border-ai/40` that
      // `outlineBase` carries.
      { variant: "ghost", color: "ai", class: `text-ai ${FILL_ROLES.ai.outlineHover}` },
      // Ghost has no border box to color, so the status hues take their ink without the
      // `border-*/40` that `outlineBase` carries.
      { variant: "ghost", color: "success", class: `text-success ${FILL_ROLES.success.outlineHover}` },
      { variant: "ghost", color: "warning", class: `text-warning-ink ${FILL_ROLES.warning.outlineHover}` },
      { variant: "ghost", color: "info", class: `text-info ${FILL_ROLES.info.outlineHover}` },
      // link — text-only, colored
      { variant: "link", color: "primary", class: "text-primary" },
      { variant: "link", color: "secondary", class: "text-secondary-foreground" },
      { variant: "link", color: "destructive", class: "text-destructive" },
      { variant: "link", color: "muted", class: "text-muted-foreground" },
      { variant: "link", color: "white", class: "text-white" },
      { variant: "link", color: "ai", class: "text-ai" },
      { variant: "link", color: "success", class: "text-success" },
      { variant: "link", color: "warning", class: "text-warning-ink" },
      { variant: "link", color: "info", class: "text-info" },
      // iconOnly — square box per size (overrides the px/gap from size)
      { iconOnly: true, size: "xs", class: "size-6" },
      { iconOnly: true, size: "sm", class: "size-7" },
      { iconOnly: true, size: "md", class: "size-9" },
      { iconOnly: true, size: "lg", class: "size-10" },
    ],
    defaultVariants: {
      variant: "solid",
      color: "primary",
      size: "md",
      iconOnly: false,
    },
  }
);

export const buttonGroupVariants = cva("inline-flex items-center", {
  variants: {
    layout: {
      // Seamless — the group owns the chrome so that *any* bordered control can be joined, not only
      // Button. Children differ in radius and border color (Button's outline roles paint
      // `border-primary/40`, Select's trigger `border-input` at a different radius), so rather than
      // subtracting from whatever each child happens to declare, the group flattens every child to
      // borderless/square and redraws the shape itself: a single outline around the whole group,
      // with no divider at the seams — the children read as one continuous control. Deliberately no
      // `overflow-hidden` to clip the corners — both Button and SelectTrigger paint
      // `focus-visible:ring-[3px]` as an outward box-shadow, which a clipping parent would swallow.
      joined:
        // Children keep their own background so hover/active feedback still paints per control;
        // only the chrome (border, radius, shadow) is surrendered to the group.
        "[&>*]:!rounded-none [&>*]:!border-transparent [&>*]:!shadow-none " +
        "[&>*:first-child]:!rounded-l-[calc(var(--radius)-2px)] " +
        "[&>*:last-child]:!rounded-r-[calc(var(--radius)-2px)] " +
        // Same chrome an individual `variant="outline"` button draws, so a group sits beside one as
        // the same family. The role tint (`border-primary/40` et al) is deliberately not replicated
        // here: on a single button it tints one control, but on the group it would tint the whole
        // row regardless of what the children's own colors are.
        "rounded-[calc(var(--radius)-2px)] border border-border bg-background shadow-xs",
      separated: "gap-2",
    },
  },
  defaultVariants: {
    layout: "joined",
  },
});
